import {
  CoverageWithPricesInfo,
  crmClient,
  getParsedCoverageWithPricesInfo,
} from '@enaratech/funnel-helper';
import { Grid, Stack } from '@mui/material';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Agreements, { RefAgreements } from 'src/components/Common/Agreements/Agreements';
import { Page, withProgress } from 'src/components/Common/Progress/Progress';
import BasicLayout from 'src/components/Layout/BasicLayout/BasicLayout';
import { useAuth } from 'src/contexts/auth';
import { useClinic } from 'src/contexts/clinic';
import { useOnboarding } from 'src/contexts/onboarding';
import { useMembershipStore } from 'src/store/memberships';
import { getEffectiveProgramType } from 'src/utils/programType';
import CoverageResult from '../Payment/CoverageResult';
import { PLAN_OPTIONS } from '../Payment/Inputs/inputFields';
import { navigateToPage } from '../routes';

const CustomAgreements: FC = () => {
  const [agreementsLoaded, setAgreementsLoaded] = useState<boolean>(false);
  const [allAgreementsSelected, setAllAgreementsSelected] = useState<boolean>(false);
  const [coverage, setCoverage] = useState<CoverageWithPricesInfo | null>(null);

  const agreementsRef = useRef<RefAgreements>(null);
  const navigate = useNavigate();

  const isMonthly = useMembershipStore((s) => s.isMonthly);

  const {
    authState: { user },
  } = useAuth();

  const { clinicState } = useClinic();

  const {
    onboardingState: {
      eligibility: { membershipType },
    },
  } = useOnboarding();

  const handleClick = (): void => {
    if (agreementsRef.current) {
      agreementsRef.current.signAgreements();
    }

    const initialPrice = isMonthly
      ? `${coverage?.originalPrices.monthly}`
      : `${coverage?.originalPrices.annual}`;

    const finalPrice = isMonthly
      ? `${coverage?.discountedPrices.monthly}`
      : `${coverage?.discountedPrices.annual}`;

    crmClient.updateLead({
      uuid: user!.uuid,
      shownPrice: initialPrice,
      finalPrice,
      billingFrequency: isMonthly ? 'monthly' : 'yearly',
    });

    navigateToPage({ targetPage: '/appointment/self-serve-booking', navigate });
  };

  const handleGetCoverage = async () => {
    const coverageResponse = await getParsedCoverageWithPricesInfo({
      membershipType,
      programType: getEffectiveProgramType({ user, clinic: clinicState })!,
      clinicId: user!.clinicId,
    });

    setCoverage(coverageResponse as CoverageWithPricesInfo);
  };

  useEffect(() => {
    handleGetCoverage();
  }, []);

  return (
    <BasicLayout
      title={'Please review and sign your updated terms to continue using Enara'}
      subtitle={'You won’t be able to continue using your Enara account until you sign the terms'}
      buttonProps={{ disabled: !agreementsLoaded || !allAgreementsSelected, onClick: handleClick }}
      className='container-payment'>
      <Grid container justifyContent={'space-around'} direction={'row-reverse'}>
        <Grid item md={12} lg={5} xl={4}>
          {coverage && (
            <CoverageResult
              planOptions={PLAN_OPTIONS}
              onCoverageChange={(value: CoverageWithPricesInfo | null) => setCoverage(value)}
              initialCoverage={coverage}
            />
          )}
        </Grid>
        <Grid item md={12} lg={6} xl={7}>
          <Stack>
            <Agreements
              ref={agreementsRef}
              agreementsMode={'needs-auth'}
              onLoaded={useCallback(() => setAgreementsLoaded(true), [])}
              onSelect={useCallback((all) => setAllAgreementsSelected(all), [])}
            />
          </Stack>
        </Grid>
      </Grid>
    </BasicLayout>
  );
};

export default withProgress(CustomAgreements, Page.CustomAgreements);
